import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Input,
  Form,
  Select,
  message,
  Card,
  Space,
  Typography,
  Spin,
  Row,
  Col,
  Statistic,
  theme,
  Tooltip,
} from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;
const { TextArea } = Input;

// ─── Permission helpers ───────────────────────────────────────────────────────
function getRoleType() {
  return localStorage.getItem("roleType") || "";
}
function isSuperadmin() {
  return getRoleType() === "SUPERADMIN";
}
function getPermissions() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}
function hasPermission(perm) {
  if (isSuperadmin()) return true;
  return getPermissions().includes(perm);
}

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_TAG = {
  PENDING: <Tag color="warning" icon={<ClockCircleOutlined />}>Pending Review</Tag>,
  FAILED: <Tag color="error" icon={<ExclamationCircleOutlined />}>Apply Failed</Tag>,
  APPLIED: <Tag color="success" icon={<CheckCircleOutlined />}>Applied</Tag>,
  REJECTED: <Tag color="default" icon={<CloseCircleOutlined />}>Rejected</Tag>,
  CANCELLED: <Tag color="default" icon={<StopOutlined />}>Cancelled</Tag>,
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function NmiChange() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [failedRequests, setFailedRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, failed: 0, applied: 0, rejected: 0, cancelled: 0 });

  // Review modal (approve / reject)
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [reviewAction, setReviewAction] = useState(null); // 'APPROVE' | 'REJECT'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewing, setReviewing] = useState(false);

  // Retry tracking
  const [retryingId, setRetryingId] = useState(null);

  // Completed table filter
  const [completedStatusFilter, setCompletedStatusFilter] = useState(null);

  // Pagination sizes
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [failedPageSize, setFailedPageSize] = useState(10);
  const [completedPageSize, setCompletedPageSize] = useState(10);

  const canReview = hasPermission("approval:review");

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/approval-requests");
      if (res.data.success) {
        const { pending = [], failed = [], completed = [], counts: c = {} } = res.data.data;
        setPendingRequests(pending);
        setFailedRequests(failed);
        setCompletedRequests(completed);
        setCounts(c);
      } else {
        message.error(res.data.message || "Failed to load approval requests");
      }
    } catch (err) {
      console.error("Error fetching approval requests:", err);
      message.error("Failed to load approval requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // ── Review modal ──────────────────────────────────────────────────────────

  const openReviewModal = (request, action) => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNote("");
    setReviewModalVisible(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedRequest) return;

    setReviewing(true);
    try {
      const res = await api.post(`/api/approval-requests/${selectedRequest.id}/review`, {
        action: reviewAction,
        review_note: reviewNote || undefined,
      });

      if (res.data.success) {
        const finalStatus = res.data.data?.status;
        if (reviewAction === "APPROVE") {
          if (finalStatus === "APPLIED") {
            message.success("Approved and applied successfully");
          } else if (finalStatus === "FAILED") {
            message.warning("Approved but apply failed — you can retry from the Failed table");
          } else {
            message.success("Approved");
          }
        } else {
          message.success("Request rejected");
        }
        setReviewModalVisible(false);
        setSelectedRequest(null);
        setReviewNote("");
        fetchRequests();
      } else {
        message.error(res.data.message || "Failed to submit review");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to submit review");
    } finally {
      setReviewing(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────

  const handleCancel = (requestId) => {
    Modal.confirm({
      title: "Cancel this request?",
      content: "This approval request will be cancelled and cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "Keep",
      onOk: async () => {
        try {
          const res = await api.post(`/api/approval-requests/${requestId}/cancel`);
          if (res.data.success) {
            message.success("Request cancelled");
            fetchRequests();
          } else {
            message.error(res.data.message || "Failed to cancel");
          }
        } catch (err) {
          message.error(err.response?.data?.message || "Failed to cancel");
        }
      },
    });
  };

  // ── Retry ─────────────────────────────────────────────────────────────────

  const handleRetry = async (requestId) => {
    setRetryingId(requestId);
    try {
      const res = await api.post(`/api/approval-requests/${requestId}/retry`);
      if (res.data.success) {
        const finalStatus = res.data.data?.status;
        if (finalStatus === "APPLIED") {
          message.success("Retry succeeded — change applied");
        } else {
          message.warning("Retry failed again — check the error in the Failed table");
        }
        fetchRequests();
      } else {
        message.error(res.data.message || "Retry failed");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  // ── Column definitions ────────────────────────────────────────────────────

  // POW-191: every column carries an explicit width.
  //
  // `scroll={{ x: true }}` alone does NOT make the table scroll — antd only
  // knows the content overflows if it can total the column widths. Without
  // them it squeezes columns to fit the viewport instead, and on a narrow
  // screen a header like "System SN" collapses to one character per line.
  // Widths + a numeric scroll.x (see the Table props) make the overflow
  // explicit, so the table scrolls horizontally and text stays on one line.
  const baseColumns = [
    {
      title: "Field",
      dataIndex: ["payload", "field"],
      key: "field",
      render: (field) => (
        <Tag color="blue">{field?.toUpperCase() || "-"}</Tag>
      ),
      width: 90,
    },
    {
      title: "System SN",
      dataIndex: "system_sn",
      key: "system_sn",
      width: 160,
      render: (text) => <Text strong>{text || "-"}</Text>,
    },
    {
      title: "Old Value",
      dataIndex: ["payload", "old_value"],
      key: "old_value",
      width: 160,
      render: (val) => <Text type="secondary">{val || "-"}</Text>,
    },
    {
      title: "New Value",
      dataIndex: ["payload", "new_value"],
      key: "new_value",
      width: 160,
      render: (val) => <Text strong>{val || "-"}</Text>,
    },
    {
      title: "Reason",
      dataIndex: "requester_note",
      key: "requester_note",
      width: 200,
      ellipsis: true,
      render: (val) => val || <Text type="secondary">-</Text>,
    },
    {
      title: "Submitted By",
      dataIndex: "requester_name",
      key: "requester_name",
      width: 150,
      ellipsis: true,
      render: (val) => val || "-",
    },
    {
      title: "Submitted",
      dataIndex: "requested_at",
      key: "requested_at",
      width: 170,
      render: (val) => formatDate(val),
    },
  ];

  // Sum of baseColumns widths (90+160+160+160+200+150+170), reused below so
  // each table's scroll.x stays in step with the columns it actually renders.
  const BASE_COLUMNS_WIDTH = 1090;

  const pendingColumns = [
    ...baseColumns,
    {
      title: "Actions",
      key: "actions",
      align: "right",
      // Fits all three buttons (Approve + Reject + Cancel) plus Space gaps and
      // cell padding — measured ~295px. A reviewer looking at their OWN request
      // sees all three at once, which 210px clipped.
      width: 300,
      fixed: "right",
      render: (_, record) => {
        const currentUserId = localStorage.getItem("userId");
        const isOwn = record.requested_by === currentUserId;
        // Neither reviewer nor owner: render a dash rather than an empty cell,
        // so the row does not look broken or still-loading.
        if (!canReview && !isOwn) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Space wrap size={[8, 8]} style={{ justifyContent: "flex-end", width: "100%" }}>
            {canReview && (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => openReviewModal(record, "APPROVE")}
                >
                  Approve
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={() => openReviewModal(record, "REJECT")}
                >
                  Reject
                </Button>
              </>
            )}
            {isOwn && (
              <Tooltip title="Cancel your request">
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  onClick={() => handleCancel(record.id)}
                >
                  Cancel
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const failedColumns = [
    ...baseColumns,
    {
      title: "Error",
      key: "error",
      width: 220,
      ellipsis: true,
      render: (_, record) => {
        const err = record.apply_result?.error;
        return err ? (
          <Tooltip title={err}>
            <Text type="danger" ellipsis>{err}</Text>
          </Tooltip>
        ) : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 120,
      fixed: "right",
      // Non-reviewers get a dash, not an empty cell (see the pending table).
      render: (_, record) =>
        canReview ? (
          <Button
            size="small"
            icon={<SyncOutlined />}
            loading={retryingId === record.id}
            onClick={() => handleRetry(record.id)}
          >
            Retry
          </Button>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  const completedColumns = [
    ...baseColumns,
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s) => STATUS_TAG[s] || <Tag>{s}</Tag>,
    },
    {
      title: "Reviewed By",
      dataIndex: "reviewer_name",
      key: "reviewer_name",
      width: 150,
      ellipsis: true,
      render: (val) => val || "-",
    },
    {
      title: "Review Note",
      dataIndex: "review_note",
      key: "review_note",
      width: 200,
      ellipsis: true,
      render: (val) => val || <Text type="secondary">-</Text>,
    },
    {
      title: "Reviewed",
      dataIndex: "reviewed_at",
      key: "reviewed_at",
      width: 170,
      render: (val) => formatDate(val),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <PageLayout active="nmiChange">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="nmiChange">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              NMI / SN Change Requests
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Review requests to change system NMI or Serial Number
            </Text>
          </div>
          <Space>
            {/* POW-184: "New Request" removed — requests are raised from the
                System Detail page. */}
            <Button icon={<ReloadOutlined />} onClick={fetchRequests}>
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {/* Stats
        * POW-191: responsive spans. A fixed span={8} keeps all three cards on
        * one row at every width, so on a narrow screen each gets a third of the
        * viewport and labels like "Apply Failed" wrap to one word — or one
        * character — per line. Full width on xs, half on sm, thirds from md up. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Pending"
              value={counts.pending || 0}
              valueStyle={{ color: token.colorWarning }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Apply Failed"
              value={counts.failed || 0}
              valueStyle={{ color: token.colorError }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Statistic
              title="Applied"
              value={counts.applied || 0}
              valueStyle={{ color: token.colorSuccess }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Pending Table */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined style={{ color: token.colorWarning }} />
            <span>Pending Review</span>
            <Tag color="warning">{pendingRequests.length}</Tag>
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 8 }}
      >
        <Table
          columns={pendingColumns}
          dataSource={pendingRequests}
          rowKey="id"
          pagination={makePagination(pendingPageSize, setPendingPageSize)}
          locale={{ emptyText: "No pending requests" }}
          scroll={{ x: BASE_COLUMNS_WIDTH + 300 }}
        />
      </Card>

      {/* Failed Table */}
      {(failedRequests.length > 0 || counts.failed > 0) && (
        <Card
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: token.colorError }} />
              <span>Apply Failed</span>
              <Tag color="error">{failedRequests.length}</Tag>
            </Space>
          }
          bordered={false}
          style={{ marginBottom: 24, borderRadius: 8 }}
        >
          <Table
            columns={failedColumns}
            dataSource={failedRequests}
            rowKey="id"
            pagination={makePagination(failedPageSize, setFailedPageSize)}
            locale={{ emptyText: "No failed requests" }}
            scroll={{ x: BASE_COLUMNS_WIDTH + 340 }}
          />
        </Card>
      )}

      {/* Completed Table */}
      <Card
        title={
          <Space>
            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            <span>Completed</span>
            <Tag color="success">
              {completedStatusFilter
                ? completedRequests.filter((r) => r.status === completedStatusFilter).length
                : completedRequests.length}
            </Tag>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: 180 }}
            value={completedStatusFilter}
            onChange={(val) => setCompletedStatusFilter(val || null)}
            options={[
              { value: "APPLIED", label: "Applied" },
              { value: "REJECTED", label: "Rejected" },
              { value: "CANCELLED", label: "Cancelled" },
            ]}
          />
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <Table
          columns={completedColumns}
          dataSource={
            completedStatusFilter
              ? completedRequests.filter((r) => r.status === completedStatusFilter)
              : completedRequests
          }
          rowKey="id"
          pagination={makePagination(completedPageSize, setCompletedPageSize)}
          locale={{ emptyText: "No completed requests" }}
          scroll={{ x: BASE_COLUMNS_WIDTH + 640 }}
        />
      </Card>

      {/* ── Review Modal ── */}
      <Modal
        title={
          <Space>
            {reviewAction === "APPROVE" ? (
              <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            ) : (
              <CloseCircleOutlined style={{ color: token.colorError }} />
            )}
            <span>{reviewAction === "APPROVE" ? "Approve Request" : "Reject Request"}</span>
          </Space>
        }
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          setSelectedRequest(null);
          setReviewNote("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setReviewModalVisible(false);
              setSelectedRequest(null);
              setReviewNote("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={reviewAction === "REJECT"}
            loading={reviewing}
            onClick={handleReviewSubmit}
          >
            {reviewAction === "APPROVE" ? "Approve & Apply" : "Reject"}
          </Button>,
        ]}
        width={480}
        centered
      >
        {selectedRequest && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                background: "#fafafa",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
                border: "1px solid #f0f0f0",
              }}
            >
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>FIELD</Text>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {selectedRequest.payload?.field?.toUpperCase() || "-"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>SYSTEM SN</Text>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {selectedRequest.system_sn || "-"}
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>OLD VALUE</Text>
                  <div style={{ marginTop: 2 }}>{selectedRequest.payload?.old_value || "-"}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11 }}>NEW VALUE</Text>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {selectedRequest.payload?.new_value || "-"}
                  </div>
                </Col>
              </Row>
              {selectedRequest.requester_note && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>REASON</Text>
                  <div style={{ marginTop: 2 }}>{selectedRequest.requester_note}</div>
                </div>
              )}
            </div>

            {reviewAction === "APPROVE" && (
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Approving will immediately apply the change to the system.
              </Text>
            )}

            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Review Note <Text type="secondary">(optional)</Text>
              </Text>
              <TextArea
                rows={3}
                placeholder="Add a note for the requester..."
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                style={{ borderRadius: 2 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </PageLayout>
  );
}
